import React, { useState } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  Briefcase, 
  Building2, 
  Lock, 
  Eye, 
  EyeOff, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  BarChart3,
  ArrowLeft,
  UserPlus,
  Search
} from 'lucide-react';
import { cleanCNPJ, formatCNPJ, isValidCNPJ, fetchCompanyInfo } from '../services/cnpjService';

const RegisterForm = ({ onRegister, onSwitchToLogin, error, supabase }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nomeCompleto: '',
    telefone: '',
    cargo: '',
    cnpj: '',
    numeroOperadora: ''
  });
  
  const [companyData, setCompanyData] = useState(null);
  const [existingCompany, setExistingCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [cnpjValidated, setCnpjValidated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'cnpj') {
      const cleanValue = cleanCNPJ(value);
      if (cleanValue.length <= 14) {
        setFormData(prev => ({
          ...prev,
          [name]: formatCNPJ(cleanValue)
        }));
        
        if (cleanValue !== cleanCNPJ(formData.cnpj)) {
          setCompanyData(null);
          setExistingCompany(null);
          setCnpjValidated(false);
        }
      }
      return;
    }
    
    if (name === 'telefone') {
      const cleanValue = value.replace(/\D/g, '');
      let formattedValue = cleanValue;
      
      if (cleanValue.length <= 11) {
        if (cleanValue.length === 11) {
          formattedValue = cleanValue.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
        } else if (cleanValue.length === 10) {
          formattedValue = cleanValue.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
        }
      } else {
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
      return;
    }
    
    if (name === 'numeroOperadora') {
      const cleanValue = value.replace(/\D/g, '');
      if (cleanValue.length <= 6) {
        setFormData(prev => ({
          ...prev,
          [name]: cleanValue
        }));
      }
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.nomeCompleto.trim()) {
        newErrors.nomeCompleto = 'Nome completo é obrigatório';
      }
      
      if (!formData.email) {
        newErrors.email = 'Email é obrigatório';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email inválido';
      }
      
      if (!formData.telefone.trim()) {
        newErrors.telefone = 'Telefone é obrigatório';
      }
      
      if (!formData.cargo.trim()) {
        newErrors.cargo = 'Cargo é obrigatório';
      }
    }
    
    if (step === 2) {
      const cleanedCnpj = cleanCNPJ(formData.cnpj);
      if (!cleanedCnpj) {
        newErrors.cnpj = 'CNPJ é obrigatório';
      } else if (!isValidCNPJ(cleanedCnpj)) {
        newErrors.cnpj = 'CNPJ inválido';
      }
      
      if (!formData.numeroOperadora) {
        newErrors.numeroOperadora = 'Número da operadora é obrigatório';
      } else if (formData.numeroOperadora.length !== 6) {
        newErrors.numeroOperadora = 'Número da operadora deve ter exatamente 6 dígitos';
      }
    }
    
    if (step === 3) {
      if (!formData.password) {
        newErrors.password = 'Senha é obrigatória';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
      }
      
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Senhas não conferem';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = async () => {
    if (!validateStep(currentStep)) {
      return;
    }
    
    if (currentStep === 2) {
      await handleContinueFromCompanyStep();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleContinueFromCompanyStep = async () => {
    setCnpjLoading(true);
    const cleanedCnpj = cleanCNPJ(formData.cnpj);

    try {
      const response = await fetchCompanyInfo(cleanedCnpj);
      setCompanyData(response.company_data);
      setExistingCompany(response.existing_company);
      setCnpjValidated(true);
      setShowConfirmModal(true);
      setErrors(prev => ({ ...prev, cnpj: '' }));
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        cnpj: error.message || 'Erro ao validar CNPJ'
      }));
      setCompanyData(null);
      setExistingCompany(null);
      setCnpjValidated(false);
    } finally {
      setCnpjLoading(false);
    }
  };

  const handleConfirmCompany = () => {
    setShowConfirmModal(false);
    setCurrentStep(3);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmModal(false);
    setCompanyData(null);
    setExistingCompany(null);
    setCnpjValidated(false);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(3)) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // IMPORTANTE: Sempre usar o numero_operadora do formulário
      const finalCompanyData = companyData ? {
        ...companyData,
        numero_operadora: formData.numeroOperadora
      } : {
        cnpj: cleanCNPJ(formData.cnpj),
        razao_social: `Empresa CNPJ ${formatCNPJ(cleanCNPJ(formData.cnpj))}`,
        numero_operadora: formData.numeroOperadora,
        nome_fantasia: '',
        situacao_cadastral: 'ATIVA'
      };
      
      await onRegister({
        userData: {
          email: formData.email,
          password: formData.password,
          nomeCompleto: formData.nomeCompleto,
          telefone: cleanCNPJ(formData.telefone),
          cargo: formData.cargo
        },
        companyData: finalCompanyData
      });
    } catch (error) {
      //Erro será tratado no componente pai
    } finally {
      setIsLoading(false);
    }
  };

  const stepTitles = [
    'Dados Pessoais',
    'Dados da Empresa', 
    'Criar Senha'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Criar Conta
          </h1>
          <p className="text-gray-600 mt-2">
            Passo {currentStep} de 3: {stepTitles[currentStep - 1]}
          </p>
        </div>
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium transition-all duration-200 ${
                  step <= currentStep
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8">
          <form onSubmit={handleSubmit}>
            {currentStep === 1 && (
              <PersonalDataStep 
                formData={formData}
                errors={errors}
                onInputChange={handleInputChange}
                onNext={nextStep}
              />
            )}

            {currentStep === 2 && (
              <CompanyDataStep
                formData={formData}
                errors={errors}
                cnpjLoading={cnpjLoading}
                onInputChange={handleInputChange}
                onNext={nextStep}
                onPrev={prevStep}
              />
            )}

            {currentStep === 3 && (
              <PasswordStep
                formData={formData}
                errors={errors}
                showPassword={showPassword}
                showConfirmPassword={showConfirmPassword}
                isLoading={isLoading}
                error={error}
                onInputChange={handleInputChange}
                onTogglePassword={() => setShowPassword(!showPassword)}
                onToggleConfirmPassword={() => setShowConfirmPassword(!showConfirmPassword)}
                onSubmit={handleSubmit}
                onPrev={prevStep}
              />
            )}
          </form>

          {showConfirmModal && (
            <CompanyConfirmModal
              companyData={companyData}
              existingCompany={existingCompany}
              onConfirm={handleConfirmCompany}
              onCancel={handleCancelConfirmation}
            />
          )}

          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 transition-colors flex items-center justify-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Já tenho uma conta</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Components
const PersonalDataStep = ({ formData, errors, onInputChange, onNext }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2 mb-6">
      <User className="h-5 w-5 text-blue-500" />
      <span>Informações Pessoais</span>
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <InputField
        label="Nome Completo"
        name="nomeCompleto"
        type="text"
        value={formData.nomeCompleto}
        onChange={onInputChange}
        error={errors.nomeCompleto}
        placeholder="Seu nome completo"
        icon={<User className="h-4 w-4" />}
      />

      <InputField
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={onInputChange}
        error={errors.email}
        placeholder="seu@email.com"
        icon={<Mail className="h-4 w-4" />}
      />

      <InputField
        label="Telefone"
        name="telefone"
        type="text"
        value={formData.telefone}
        onChange={onInputChange}
        error={errors.telefone}
        placeholder="(11) 99999-9999"
        icon={<Phone className="h-4 w-4" />}
      />

      <InputField
        label="Cargo"
        name="cargo"
        type="text"
        value={formData.cargo}
        onChange={onInputChange}
        error={errors.cargo}
        placeholder="Ex: Analista, Gerente"
        icon={<Briefcase className="h-4 w-4" />}
      />
    </div>

    <button
      type="button"
      onClick={onNext}
      className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:scale-105"
    >
      Continuar
    </button>
  </div>
);

const CompanyDataStep = ({ 
  formData, errors, cnpjLoading,
  onInputChange, onNext, onPrev 
}) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2 mb-6">
      <Building2 className="h-5 w-5 text-green-500" />
      <span>Dados da Empresa</span>
    </h3>

    <InputField
      label="CNPJ"
      name="cnpj"
      type="text"
      value={formData.cnpj}
      onChange={onInputChange}
      error={errors.cnpj}
      placeholder="XX.XXX.XXX/XXXX-XX"
      icon={<Building2 className="h-4 w-4" />}
    />

    <InputField
      label="Número da Operadora"
      name="numeroOperadora"
      type="text"
      value={formData.numeroOperadora}
      onChange={onInputChange}
      error={errors.numeroOperadora}
      placeholder="123456"
      maxLength="6"
      icon={<BarChart3 className="h-4 w-4" />}
      helperText="6 dígitos do registro ANS. Este número deve corresponder aos 6 primeiros dígitos dos arquivos XTE/XTR."
    />

    {!cnpjLoading ? (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-700">
            Ao clicar em <strong>Continuar</strong>, validaremos o CNPJ e solicitaremos sua confirmação.
          </p>
        </div>
      </div>
    ) : (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-center space-x-3">
          <Loader2 className="h-5 w-5 text-yellow-600 animate-spin flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-yellow-800">Validando CNPJ...</p>
            <p className="text-xs text-yellow-700 mt-1">Consultando Receita Federal. Isso pode levar alguns segundos.</p>
          </div>
        </div>
      </div>
    )}

    <div className="flex gap-4">
      <button
        type="button"
        onClick={onPrev}
        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-4 rounded-xl transition-all duration-200"
      >
        Voltar
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={cnpjLoading}
        className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 flex items-center justify-center space-x-2"
      >
        {cnpjLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Validando...</span>
          </>
        ) : (
          <span>Continuar</span>
        )}
      </button>
    </div>
  </div>
);

const PasswordStep = ({ 
  formData, errors, showPassword, showConfirmPassword, isLoading, error,
  onInputChange, onTogglePassword, onToggleConfirmPassword, onSubmit, onPrev 
}) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2 mb-6">
      <Lock className="h-5 w-5 text-purple-500" />
      <span>Criar Senha de Acesso</span>
    </h3>

    <div className="space-y-4">
      <PasswordField
        label="Senha"
        name="password"
        value={formData.password}
        onChange={onInputChange}
        error={errors.password}
        placeholder="Mínimo 6 caracteres"
        showPassword={showPassword}
        onToggle={onTogglePassword}
      />

      <PasswordField
        label="Confirmar Senha"
        name="confirmPassword"
        value={formData.confirmPassword}
        onChange={onInputChange}
        error={errors.confirmPassword}
        placeholder="Confirme sua senha"
        showPassword={showConfirmPassword}
        onToggle={onToggleConfirmPassword}
      />
    </div>

    {error && (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 animate-in slide-in-from-top-5">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      </div>
    )}

    <div className="flex gap-4">
      <button
        type="button"
        onClick={onPrev}
        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-4 rounded-xl transition-all duration-200"
      >
        Voltar
      </button>
      <button
        type="submit"
        disabled={isLoading}
        className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 flex items-center justify-center space-x-2"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Criando conta...</span>
          </>
        ) : (
          <>
            <UserPlus className="h-5 w-5" />
            <span>Criar Conta</span>
          </>
        )}
      </button>
    </div>
  </div>
);

// Utility Components
const InputField = ({ label, name, type, value, onChange, error, placeholder, icon, maxLength, helperText }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-gray-700 flex items-center space-x-2">
      <span className="text-gray-400">{icon}</span>
      <span>{label}</span>
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      maxLength={maxLength}
      className={`w-full px-4 py-3 bg-white/70 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-400 ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-200'
      }`}
      placeholder={placeholder}
    />
    {helperText && !error && (
      <p className="text-gray-500 text-xs flex items-center space-x-1">
        <AlertCircle className="h-3 w-3" />
        <span>{helperText}</span>
      </p>
    )}
    {error && (
      <p className="text-red-600 text-sm flex items-center space-x-1">
        <AlertCircle className="h-3 w-3" />
        <span>{error}</span>
      </p>
    )}
  </div>
);

const PasswordField = ({ label, name, value, onChange, error, placeholder, showPassword, onToggle }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-gray-700 flex items-center space-x-2">
      <Lock className="h-4 w-4 text-gray-400" />
      <span>{label}</span>
    </label>
    <div className="relative">
      <input
        type={showPassword ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        className={`w-full px-4 py-3 pr-12 bg-white/70 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-400 ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-200'
        }`}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
      >
        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
    {error && (
      <p className="text-red-600 text-sm flex items-center space-x-1">
        <AlertCircle className="h-3 w-3" />
        <span>{error}</span>
      </p>
    )}
  </div>
);

const CompanyConfirmModal = ({ companyData, existingCompany, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <Building2 className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Essa é sua empresa?
        </h3>
      </div>

      {companyData && (
        <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2">
          <div className="text-sm">
            <p className="text-gray-600">Razão Social:</p>
            <p className="font-semibold text-gray-900">{companyData.razao_social}</p>
          </div>
          {companyData.nome_fantasia && (
            <div className="text-sm">
              <p className="text-gray-600">Nome Fantasia:</p>
              <p className="font-semibold text-gray-900">{companyData.nome_fantasia}</p>
            </div>
          )}
          <div className="text-sm">
            <p className="text-gray-600">Situação:</p>
            <p className="font-semibold text-gray-900">{companyData.situacao_cadastral}</p>
          </div>
          {companyData.endereco_cidade && companyData.endereco_uf && (
            <div className="text-sm">
              <p className="text-gray-600">Localização:</p>
              <p className="font-semibold text-gray-900">{companyData.endereco_cidade}/{companyData.endereco_uf}</p>
            </div>
          )}
        </div>
      )}

      {existingCompany && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-blue-800 mb-1">Empresa já cadastrada</h4>
              <p className="text-sm text-blue-700">
                Você será vinculado à empresa existente.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-xl transition-all duration-200"
        >
          Não, corrigir
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium rounded-xl transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
        >
          <CheckCircle2 className="h-5 w-5" />
          <span>Sim, confirmar</span>
        </button>
      </div>
    </div>
  </div>
);

export default RegisterForm;